import { entityLabel } from '../../../../terminology/store';
import { displayText } from '../../../../terminology/store';
import { useTerminology } from '../../../../terminology/TerminologyContext';
// AddClient.jsx
import React, { useState, useEffect, useContext, useRef, forwardRef } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import api, { getCsrfToken, createClientAdmin } from '../../../services/api';
import ThemeContext from '../../../ThemeContext';
import * as Yup from 'yup';
import { parsePhoneNumber } from 'libphonenumber-js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import ResellerConfig from '../../../../config/ResellerConfig';
import { URL_SEGMENTS } from '../../../../config/Routesconfig';

// ── Validation schemas ────────
const basicSchema = Yup.object({
  client_setting:       Yup.string().oneOf(['Active', 'Deactivated']).required(() => displayText('Please select a client setting.')),
  sales_representative: Yup.string().optional(),
  location:             Yup.string().optional(),
  client_name:          Yup.string().required(() => displayText('Client Name is required.')),
  start_date:           Yup.date().nullable().required('Start Date is required.'),
  primary_name:         Yup.string().required('Primary Name is required.'),
  primary_email:        Yup.string().email('Invalid email format.').required('Email is required.'),
  primary_phone:        Yup.string().required('Mobile is required.').test('valid-mobile', 'Invalid mobile number.', (value) => {
    if (!value) return false;
    try {
      const p = parsePhoneNumber(value.startsWith('+') ? value : `+${value}`);
      return p && p.isValid();
    } catch { return false; }
  }),
  country:  Yup.string().required('Country is required.'),
  address:  Yup.string().required('Address is required.'),
  city:     Yup.string().required('City is required.'),
  state:    Yup.string().required('State is required.'),
  zip:      Yup.string().required('Zip is required.').matches(/^\d+$/, 'Zip must contain only numbers.'),
  comments: Yup.string().optional(),
});

const adminSchema = Yup.object({
  first_name: Yup.string().required('First Name is required.'),
  last_name:  Yup.string().required('Last Name is required.'),
  email:      Yup.string().email('Invalid email format.').required('Email is required.'),
});

// ───────────────
const AddClient = () => {
  useTerminology();
  const { theme }    = useContext(ThemeContext);
  // NOTE: 'username' is the reseller/admin username param; 'id' is only used
  // when arriving from a reseller detail page (/superadmin/reseller/:id/add-client)
  const fontFamily = theme?.typography?.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif';
const bodyFontSize = theme?.typography?.bodyFontSize || 14;
const smallFontSize = Math.max(bodyFontSize - 2, 10);
  const { username, id } = useParams();
  const navigate         = useNavigate();
  const location         = useLocation();

  // ── Config ──
  const cfg           = ResellerConfig.clients;
  const enterpriseCfg = ResellerConfig.enterpriseClient;

  const [currentUser] = useState(JSON.parse(localStorage.getItem('user')) || {});
  const isSuperAdmin  = currentUser.role === 'superadmin';
  const isAdmin       = currentUser.role === 'admin';
  const isReseller    = currentUser.role === 'reseller';

  // ── isFromReseller: arrived via /superadmin/reseller/:id/add-client ───────
  const isFromReseller = location.pathname.includes('/reseller/') &&
                         location.pathname.includes('/add-client');

  // ── URL prefix for navigation ─
  // For superadmin/admin use role prefix; for reseller use their username.
  const resolvedUsername = username || currentUser.username || '';
  const prefix = isSuperAdmin
    ? '/superadmin'
    : isAdmin
    ? '/admin'
    : `/${resolvedUsername}`;

  const [resellerName] = useState(location.state?.reseller_name || '');
  const today          = new Date();

  const emptyForm = () => ({
    client_setting: 'Active', sales_representative: '', location: '',
    client_name: '', start_date: today.toISOString().split('T')[0],
    primary_name: '', primary_email: '', primary_phone: '',
    country: '', address: '', city: '', state: '', zip: '',
    comments: '', first_name: '', last_name: '', email: '', reseller_id: '',
  });

  const [formData,          setFormData]          = useState(emptyForm());
  const [errors,            setErrors]            = useState({});
  const [activeTab,         setActiveTab]         = useState('basic');
  const [addressCountries,  setAddressCountries]  = useState([]);
  const [countryMap,        setCountryMap]        = useState({});
  const [statesList,        setStatesList]        = useState([]);
  const [isLoading,         setIsLoading]         = useState(false);
  const [checking,          setChecking]          = useState({ email: false, mobile: false });
  const [phoneInputKey,     setPhoneInputKey]     = useState(0);
  const [isClientsDropdownOpen, setIsClientsDropdownOpen] = useState(false);

  const datePickerRef      = useRef(null);
  const clientsDropdownRef = useRef(null);

  // ── Effects ─
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res  = await fetch('https://countriesnow.space/api/v0.1/countries');
        const data = await res.json();
        if (data.error) throw new Error('Failed');
        const sorted = data.data.sort((a, b) => a.country.localeCompare(b.country));
        setAddressCountries(sorted.map((c) => ({ name: c.country, code: c.iso2 })));
        const map = {};
        data.data.forEach((c) => { map[c.iso2] = c.country; });
        setCountryMap(map);
      } catch {
        setAddressCountries([]);
        setCountryMap({});
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    if (currentUser.role === 'client') {
      toast.error('Unauthorized access', { autoClose: 3000 });
      navigate(`/${currentUser.username}/dashboard`, { replace: true });
    }
  }, [currentUser.role, navigate]);

  useEffect(() => {
    const fetchCsrf = async () => { try { await getCsrfToken(); } catch {} };
    fetchCsrf();
    setFormData((prev) => ({
      ...prev,
      reseller_id: location.state?.reseller_id
        ?? (isSuperAdmin || isAdmin ? null : isReseller ? currentUser.id : currentUser.reseller_id),
    }));
  }, [currentUser.id, currentUser.reseller_id, isSuperAdmin, isAdmin, isReseller, location.state]);

  useEffect(() => {
    const fetchStates = async () => {
      if (!formData.country) { setStatesList([]); return; }
      const countryName = countryMap[formData.country] || '';
      if (!countryName) { setStatesList([]); return; }
      try {
        const res  = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: countryName }),
        });
        const data = await res.json();
        if (data.error || !data.data) throw new Error('Failed');
        setStatesList((data.data.states || []).map((s) => s.name).sort());
      } catch { setStatesList([]); }
    };
    fetchStates();
    setFormData((prev) => ({ ...prev, state: '' }));
  }, [formData.country, countryMap]);

  useEffect(() => {
    const handle = (e) => {
      if (clientsDropdownRef.current && !clientsDropdownRef.current.contains(e.target))
        setIsClientsDropdownOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // ── Handlers 
  const handleApiResponse = async (promise, field) => {
    try {
      const res = await promise;
      if (res.data?.error)  throw new Error(res.data.error);
      if (res.data?.exists) {
        setErrors((p) => ({ ...p, [field]: `${field.replace('_', ' ')} already exists.` }));
        return false;
      }
      setErrors((p) => ({ ...p, [field]: '' }));
      return true;
    } catch (err) {
      setErrors((p) => ({ ...p, [field]: err.response?.data?.message || `${field.replace('_', ' ')} check failed.` }));
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target || {};
    const updated = { ...formData, [name]: type === 'checkbox' ? checked : value };
    if (name === 'primary_email') updated.email         = value;
    if (name === 'email')         updated.primary_email = value;
    setFormData(updated);
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const handleBlur = async (e) => {
    const { name, value } = e.target;
    if (name === 'primary_phone') return;
    try {
      const schema = ['first_name', 'last_name', 'email'].includes(name) ? adminSchema : basicSchema;
      await schema.validateAt(name, { ...formData, [name]: value });
      setErrors((p) => ({ ...p, [name]: '' }));
      if (name === 'primary_email' || name === 'email') {
        setChecking((p) => ({ ...p, email: true }));
        await handleApiResponse(api.post('/check-email', { email: value }), name);
        setChecking((p) => ({ ...p, email: false }));
      }
    } catch (err) { setErrors((p) => ({ ...p, [name]: err.message })); }
  };

  const validateField = async (field) => {
    try {
      const schema = ['first_name', 'last_name', 'email'].includes(field) ? adminSchema : basicSchema;
      await schema.validateAt(field, { ...formData });
      setErrors((p) => ({ ...p, [field]: '' }));
      return true;
    } catch (err) {
      setErrors((p) => ({ ...p, [field]: err.message }));
      return false;
    }
  };

  const validateTab = async (tab) => {
    const schema = tab === 'basic' ? basicSchema : adminSchema;
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (ve) {
      const errs = {};
      ve.inner.forEach((e) => { errs[e.path] = e.message; });
      setErrors(errs);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await validateTab('basic')) || !(await validateTab('admin'))) {
      toast.error('Please fill all required fields correctly.', { autoClose: 3000 });
      return;
    }
    setIsLoading(true);
    try {
      const payload = { ...formData };
      if (isReseller) delete payload.reseller_id;
      const response = await createClientAdmin(payload);
      if (response.data?.error) throw new Error(response.data.error);
      setIsLoading(false);
      toast.success(response.data.message || `${cfg.label} created successfully.`, { autoClose: 3000 });
      setFormData(emptyForm());
      setErrors({});
      setPhoneInputKey((p) => p + 1);
      setTimeout(() => {
        navigate(
          isSuperAdmin ? `/superadmin/${URL_SEGMENTS.clients}`
          : isAdmin    ? `/admin/${URL_SEGMENTS.clients}`
          :              `/${resolvedUsername}/${URL_SEGMENTS.clients}`
        );
      }, 3000);
    } catch (err) {
      setIsLoading(false);
      let msg = err.response?.data?.message || err.message || `Failed to create ${cfg.label.toLowerCase()}.`;
      if (err.response?.data?.errors) {
        const d = Object.values(err.response.data.errors).flat().join(' ');
        msg = `${msg}: ${d}`;
        setErrors(err.response.data.errors);
      }
      toast.error(msg, { autoClose: 5000 });
    }
  };

  const handleCancel = () => {
    setFormData({
      ...emptyForm(),
      reseller_id: isSuperAdmin || isAdmin ? null
                   : isReseller ? currentUser.id
                   : currentUser.reseller_id,
    });
    setErrors({});
    setPhoneInputKey((p) => p + 1);
    if (isFromReseller) {
      navigate(`${isSuperAdmin ? '/superadmin' : '/admin'}/reseller/${id}`);
    } else {
      navigate(
        isSuperAdmin ? `/superadmin/${URL_SEGMENTS.clients}`
        : isAdmin    ? `/admin/${URL_SEGMENTS.clients}`
        :              `/${resolvedUsername}/${URL_SEGMENTS.clients}`
      );
    }
  };

  const handleNext = async () => {
    if (activeTab === 'basic' && !(await validateTab('basic'))) {
      toast.error('Please fill all required fields correctly.', { autoClose: 3000 });
      return;
    }
    if (activeTab === 'basic') { setActiveTab('admin'); setErrors({}); }
  };

  const handlePrevious = () => {
    if (activeTab === 'admin') setActiveTab('basic');
    setErrors({});
  };

  const handleSubTabClick = (subTab) => {
    navigate(
      subTab === 'enterprise'
        ? `${prefix}/${URL_SEGMENTS.enterpriseClients}`
        : `${prefix}/${URL_SEGMENTS.clients}`
    );
    setIsClientsDropdownOpen(false);
  };

  // ── Custom date input ──────
  const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
    <div className="input-group date-input-group" style={{ position: 'relative' }}>
      <input
        className={`form-control ${errors.start_date ? 'is-invalid' : ''}`}
        style={{
          backgroundColor: theme.colors.bgPrimary, color: '#666',
          border: `1px solid ${errors.start_date ? theme.colors.danger : theme.colors.borderColor}`,
          fontSize: '12px', fontFamily: 'inherit',
        }}
        value={value} onClick={onClick}
        onMouseEnter={() => datePickerRef.current?.setOpen(true)}
        ref={ref}
      />
      <span
        className="input-group-text calendar-icon"
        onClick={onClick}
        onMouseEnter={() => datePickerRef.current?.setOpen(true)}
        style={{
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.textPrimary, cursor: 'pointer',
          borderLeft: 'none', zIndex: 1, position: 'absolute', right: 0, top: 0,
          height: '100%', display: 'flex', alignItems: 'center', padding: '0 8px',
        }}
      >
        <i className="bi bi-calendar-event" style={{ pointerEvents: 'none' }} />
      </span>
    </div>
  ));
  CustomDateInput.displayName = 'CustomDateInput';

  const inputStyle = (field) => ({
    width: '100%',
    padding: '0.5rem',
    borderRadius: '4px',
    backgroundColor: theme.colors.bgPrimary,
    color: theme.colors.textPrimary,
    border: `1px solid ${errors[field] ? theme.colors.danger : theme.colors.borderColor}`,
  });

  // ── Nav tabs (shown only when isFromReseller) ────────────────────────────
  const mainTabs = isSuperAdmin
    ? ['resellers', 'clients', 'reports', 'notify', 'admins', 'account']
    : ['resellers', 'clients', 'reports', 'account'];

  const tabLabels = {
    resellers: displayText('Resellers'),
    clients:   ResellerConfig.nav.clients.label,
    reports:   ResellerConfig.nav.reports.label,
    notify:    'Notify',
    admins:    'Admins',
    account:   ResellerConfig.nav.account.label,
  };
  const tabIcons = {
    resellers: 'bi-briefcase-fill',
    clients:   `bi-${ResellerConfig.nav.clients.icon}`,
    reports:   `bi-${ResellerConfig.nav.reports.icon}`,
    notify:    'bi-bell-fill',
    admins:    'bi-buildings-fill',
    account:   `bi-${ResellerConfig.nav.account.icon}`,
  };
  const activeMainTab = isFromReseller ? 'resellers' : 'clients';

  // ───────────
  return (
    <div style={{ fontFamily, fontSize: `${bodyFontSize}px`, color: theme.colors.textPrimary }}>
      <style>{`
        select:focus { outline: none !important; box-shadow: none !important;
          border-color: ${errors.country || errors.state ? theme.colors.danger : '#000'} !important; }
        input[name="location"]:focus { outline: none !important; box-shadow: none !important;
          border-color: ${errors.location ? theme.colors.danger : '#000'} !important; }
      `}</style>

      {/* ── Top nav (only when from reseller detail page) ── */}
      {isFromReseller && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          {mainTabs.map((tab) => (
            <div
              key={tab}
              style={{ position: 'relative', flex: '1 1 0', minWidth: '100px' }}
              ref={tab === 'clients' ? clientsDropdownRef : null}
              onMouseEnter={() => tab === 'clients' && setIsClientsDropdownOpen(true)}
              onMouseLeave={() => tab === 'clients' && setIsClientsDropdownOpen(false)}
            >
              <Link
                to={tab !== 'clients' ? `${prefix}/${tab}` : '#'}
                style={{
                  width: '100%', padding: '0.4rem',
                  fontSize: 'clamp(0.65rem,2vw,0.8rem)', fontWeight: 'bold',
                  textAlign: 'center', borderRadius: '5px',
                  background: tab === activeMainTab ? theme.gradients.primary : theme.gradients.secondary,
                  color: theme.colors.textWhite,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  gap: '0.5rem', textDecoration: 'none',
                }}
                onClick={(e) => {
                  if (tab === 'clients') { e.preventDefault(); setIsClientsDropdownOpen((p) => !p); }
                }}
              >
                <i className={`bi ${tabIcons[tab]}`} />
                {tabLabels[tab]}
              </Link>

              {tab === 'clients' && isClientsDropdownOpen && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0,
                  backgroundColor: theme.colors.bgPrimary,
                  border: `1px solid ${theme.colors.borderColor}`,
                  borderRadius: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                  zIndex: 10, width: '100%', minWidth: '100px',
                }}>
                  {[
                    { key: 'core',       label: cfg.label           },
                    { key: 'enterprise', label: enterpriseCfg.label },
                  ].map((sub) => (
                    <button
                      key={sub.key}
                      style={{
                        width: '100%', padding: '0.5rem 1rem', textAlign: 'center',
                        background: 'transparent', color: theme.colors.textPrimary,
                        border: 'none', cursor: 'pointer', fontSize: 'clamp(0.65rem,2vw,0.8rem)',
                      }}
                      onClick={() => handleSubTabClick(sub.key)}
                      onMouseOver={(e) => (e.target.style.background = theme.gradients.secondary)}
                      onMouseOut={(e)  => (e.target.style.background = 'transparent')}
                    >
                      {sub.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Breadcrumb ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          {isFromReseller ? (
            <>
              <Link to={`${prefix}/${URL_SEGMENTS.resellers}`} style={{ color: theme.colors.primary, textDecoration: 'underline' }}>{displayText(" Reseller Management ")}</Link>
              <span style={{ margin: '0 0.5rem', color: theme.colors.textPrimary }}>&gt;</span>
              <span style={{ color: theme.colors.textPrimary }}>{resellerName || displayText('Reseller')}</span>
              <span style={{ margin: '0 0.5rem', color: theme.colors.textPrimary }}>&gt;</span>
              <span style={{ color: theme.colors.textPrimary }}>{ResellerConfig.pageTitles.addClient}</span>
            </>
          ) : (
            <>
              <Link
                to={
                  isSuperAdmin ? `/superadmin/${URL_SEGMENTS.clients}`
                  : isAdmin    ? `/admin/${URL_SEGMENTS.clients}`
                  :              `/${resolvedUsername}/${URL_SEGMENTS.clients}`
                }
                style={{ color: theme.colors.primary, textDecoration: 'underline' }}
              >
                {ResellerConfig.pageTitles.clientList}
              </Link>
              <span style={{ margin: '0 0.5rem', color: theme.colors.textPrimary }}>&gt;</span>
              <span style={{ color: theme.colors.textPrimary }}>{ResellerConfig.pageTitles.addClient}</span>
            </>
          )}
        </div>
      </div>

      {/* ── Main Container ── */}
      <div style={{
        backgroundColor: theme.colors.bgPrimary, borderRadius: '12px',
        padding: '1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }}>
        <h4 style={{ color: theme.colors.textPrimary, marginBottom: '1.5rem' }}>
          {ResellerConfig.pageTitles.addClient}
        </h4>

        <ToastContainer position="top-right" autoClose={3000} theme="light"
          toastStyle={{
            backgroundColor: theme.colors.bgPrimary,
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
          }}
        />

        {isLoading && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            <div style={{ border: `4px solid ${theme.colors.primary}`, borderTop: '4px solid transparent', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite' }} />
          </div>
        )}

        <div style={{ border: `1px solid ${theme.colors.borderColor}`, borderRadius: '8px', padding: '1.5rem', backgroundColor: '#fff', position: 'relative' }}>

          {/* Tab Controls */}
          <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '2rem' }}>
            {['basic', 'admin'].map((tab, index) => (
              <a
                key={tab} href="#"
                style={{
                  color: activeTab === tab ? theme.colors.primary : theme.colors.secondary,
                  textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem',
                }}
                onClick={async (e) => {
                  e.preventDefault();
                  if (activeTab === 'basic' && tab === 'admin' && !(await validateTab('basic'))) {
                    toast.error('Please fill all required fields correctly.', { autoClose: 3000 });
                    return;
                  }
                  setActiveTab(tab);
                  setErrors({});
                }}
              >
                <span style={{
                  border: `2px solid ${activeTab === tab ? theme.colors.primary : theme.colors.secondary}`,
                  color: activeTab === tab ? theme.colors.textWhite : theme.colors.secondary,
                  backgroundColor: activeTab === tab ? theme.colors.primary : 'transparent',
                  width: '28px', height: '28px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold',
                }}>
                  {index + 1}
                </span>
                <span style={{ borderBottom: activeTab === tab ? `2px solid ${theme.colors.primary}` : 'none', paddingBottom: '4px' }}>
                  {tab === 'basic' ? 'Basic Information' : `${entityLabel('clientAdmin', 'singular', 'Admin')} Setup`}
                </span>
              </a>
            ))}
          </div>

          {/* ── BASIC TAB ── */}
          {activeTab === 'basic' && (
            <div>
              <p className="fw-bold" style={{ color: theme.colors.textPrimary }}>
                {cfg.label} Default Settings
              </p>

              {/* Location */}
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>Location</label>
                  <input type="text" name="location" value={formData.location}
                    onChange={handleInputChange} onBlur={handleBlur}
                    className={`form-control ${errors.location ? 'is-invalid' : ''}`}
                    style={inputStyle('location')} />
                  {errors.location && <span className="invalid-feedback" style={{ color: theme.colors.danger }}>{errors.location}</span>}
                </div>
              </div>

              {/* Client Setting */}
              <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                <label className="input-label" style={{ color: theme.colors.textPrimary }}>{cfg.label} Settings*</label>
                <div className="d-flex align-items-center">
                  {['Active', 'Deactivated'].map((s) => (
                    <div key={s} className={`form-check ${s === 'Active' ? 'me-3' : ''}`}>
                      <input type="radio" name="client_setting" value={s}
                        checked={formData.client_setting === s} onChange={handleInputChange}
                        className="form-check-input" id={`client${s}`} />
                      <label className="form-check-label" style={{ color: theme.colors.textPrimary }} htmlFor={`client${s}`}>{s}</label>
                    </div>
                  ))}
                </div>
                {errors.client_setting && <span className="invalid-feedback d-block" style={{ color: theme.colors.danger }}>{errors.client_setting}</span>}
              </div>

              {/* Client Name + Start Date */}
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>{cfg.label} Name*</label>
                  <input type="text" name="client_name" value={formData.client_name}
                    onChange={handleInputChange} onBlur={handleBlur}
                    className={`form-control ${errors.client_name ? 'is-invalid' : ''}`}
                    style={inputStyle('client_name')} required />
                  {errors.client_name && <span className="invalid-feedback" style={{ color: theme.colors.danger }}>{errors.client_name}</span>}
                </div>
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>Start Date*</label>
                  <DatePicker
                    ref={datePickerRef}
                    selected={formData.start_date ? new Date(formData.start_date) : null}
                    onChange={(date) => {
                      setFormData((p) => ({ ...p, start_date: date ? date.toISOString().split('T')[0] : null }));
                      if (errors.start_date) setErrors((p) => ({ ...p, start_date: '' }));
                      if (date) validateField('start_date');
                    }}
                    dateFormat="yyyy-MM-dd" minDate={today}
                    onFocus={() => datePickerRef.current?.setOpen(true)}
                    onBlur={() => validateField('start_date')}
                    onCalendarClose={() => validateField('start_date')}
                    wrapperClassName="date-input-container" calendarClassName="custom-date-picker"
                    customInput={<CustomDateInput />} required
                  />
                  {errors.start_date && <div className="invalid-feedback d-block" style={{ color: theme.colors.danger, fontSize: '0.875em' }}>{errors.start_date}</div>}
                </div>
              </div>

              {/* Primary Name + Email */}
              {[
                { label: 'Primary Contact (Name)*',  name: 'primary_name',  type: 'text' },
                { label: 'Primary Contact (Email)*', name: 'primary_email', type: 'text' },
              ].map(({ label, name, type }) => (
                <div key={name} className="row">
                  <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                    <label className="input-label" style={{ color: theme.colors.textPrimary }}>{label}</label>
                    <input type={type} name={name} value={formData[name]}
                      onChange={handleInputChange} onBlur={handleBlur}
                      className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
                      style={inputStyle(name)} required />
                    {errors[name] && <span className="invalid-feedback" style={{ color: theme.colors.danger }}>{errors[name]}</span>}
                    {name === 'primary_email' && checking.email && (
                      <span style={{ color: theme.colors.primary, display: 'block', marginTop: '0.3rem' }}>Checking availability...</span>
                    )}
                  </div>
                </div>
              ))}

              {/* Phone */}
              <div className="row">
                <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>Primary Contact (Mobile)*</label>
                  <PhoneInput
                    key={phoneInputKey}
                    country={'us'}
                    value={formData.primary_phone}
                    onChange={(value) => {
                      const p = value.startsWith('+') ? value : `+${value}`;
                      setFormData((prev) => ({ ...prev, primary_phone: p }));
                      if (errors.primary_phone) setErrors((prev) => ({ ...prev, primary_phone: '' }));
                    }}
                    onBlur={async () => {
                      const val = formData.primary_phone;
                      if (!val) { setErrors((p) => ({ ...p, primary_phone: 'Mobile is required.' })); return; }
                      try {
                        const pn = parsePhoneNumber(val.startsWith('+') ? val : `+${val}`);
                        if (!pn || !pn.isValid()) { setErrors((p) => ({ ...p, primary_phone: 'Invalid mobile number.' })); return; }
                        setErrors((p) => ({ ...p, primary_phone: '' }));
                        setChecking((p) => ({ ...p, mobile: true }));
                        await handleApiResponse(api.post('/check-phone', { phone: val.startsWith('+') ? val : `+${val}` }), 'primary_phone');
                        setChecking((p) => ({ ...p, mobile: false }));
                      } catch {
                        setErrors((p) => ({ ...p, primary_phone: 'Invalid mobile number.' }));
                        setChecking((p) => ({ ...p, mobile: false }));
                      }
                    }}
                    inputClass={`form-control ${errors.primary_phone ? 'is-invalid' : ''}`}
                    inputStyle={{ width: '100%', height: '35px', fontFamily: 'inherit', backgroundColor: theme.colors.bgPrimary, color: theme.colors.textPrimary, border: `1px solid ${errors.primary_phone ? theme.colors.danger : theme.colors.borderColor}`, borderRadius: '4px', padding: '6px 10px 6px 45px' }}
                    containerStyle={{ width: '100%' }}
                    inputProps={{ id: 'primary_phone', name: 'primary_phone', required: true }}
                    buttonStyle={{ border: `1px solid ${theme.colors.borderColor}`, backgroundColor: theme.colors.bgPrimary, borderRight: 'none', borderRadius: '4px 0 0 4px', height: '35px' }}
                  />
                  {errors.primary_phone && <div className="invalid-feedback d-block" style={{ color: theme.colors.danger, fontSize: '0.875em' }}>{errors.primary_phone}</div>}
                  {checking.mobile && <span style={{ color: theme.colors.primary, display: 'block', marginTop: '0.25rem', fontSize: '0.875em' }}>Checking availability...</span>}
                </div>
              </div>

              {/* Country + Address */}
              {[
                { label: 'Country*', name: 'country', type: 'select' },
                { label: 'Address*', name: 'address', type: 'text'   },
              ].map(({ label, name, type }) => (
                <div key={name} className="row">
                  <div className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                    <label className="input-label" style={{ color: theme.colors.textPrimary }}>{label}</label>
                    {type === 'select' ? (
                      <select name={name} value={formData[name]}
                        onChange={handleInputChange} onBlur={handleBlur}
                        className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
                        style={inputStyle(name)} required>
                        <option value="">Select Country</option>
                        {addressCountries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                      </select>
                    ) : (
                      <input type={type} name={name} value={formData[name]}
                        onChange={handleInputChange} onBlur={handleBlur}
                        className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
                        style={inputStyle(name)} required />
                    )}
                    {errors[name] && <span className="invalid-feedback" style={{ color: theme.colors.danger }}>{errors[name]}</span>}
                  </div>
                </div>
              ))}

              {/* City / State / Zip */}
              <div className="row">
                {[
                  { label: 'City*',  name: 'city',  type: 'text'   },
                  { label: 'State*', name: 'state', type: 'select' },
                  { label: 'Zip*',   name: 'zip',   type: 'text'   },
                ].map(({ label, name, type }) => (
                  <div key={name} className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                    <label className="input-label" style={{ color: theme.colors.textPrimary }}>{label}</label>
                    {type === 'select' ? (
                      <select name={name} value={formData[name]}
                        onChange={handleInputChange} onBlur={handleBlur}
                        disabled={!formData.country}
                        className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
                        style={inputStyle(name)} required>
                        <option value="">Select State</option>
                        {statesList.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <input type={type} name={name} value={formData[name]}
                        onChange={handleInputChange} onBlur={handleBlur}
                        className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
                        style={inputStyle(name)} required />
                    )}
                    {errors[name] && <span className="invalid-feedback" style={{ color: theme.colors.danger }}>{errors[name]}</span>}
                  </div>
                ))}
              </div>

              {/* Comments */}
              <div className="row">
                <div className="col-md-6 mb-3" style={{ marginLeft: '8px' }}>
                  <label className="input-label" style={{ color: theme.colors.textPrimary }}>Comments</label>
                  <textarea name="comments" value={formData.comments}
                    onChange={handleInputChange} onBlur={handleBlur} rows="2"
                    className={`form-control ${errors.comments ? 'is-invalid' : ''}`}
                    style={inputStyle('comments')} />
                  {errors.comments && <span className="invalid-feedback" style={{ color: theme.colors.danger }}>{errors.comments}</span>}
                </div>
              </div>

              <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button style={{ background: theme.gradients.secondary, color: theme.colors.textWhite, padding: '0.5rem 1.5rem', border: 'none', borderRadius: '5px', cursor: 'pointer' }} onClick={handleCancel}>Cancel</button>
                <button style={{ background: theme.gradients.primary,   color: theme.colors.textWhite, padding: '0.5rem 1.5rem', border: 'none', borderRadius: '5px', cursor: 'pointer' }} onClick={handleNext}>Next</button>
              </div>
            </div>
          )}

          {/* ── ADMIN TAB ── */}
          {activeTab === 'admin' && (
            <div>
              <h5 style={{ color: theme.colors.textPrimary, marginBottom: '1.5rem' }}>Setup Login Credentials</h5>
              <div className="row">
                {[
                  { label: 'First Name*', name: 'first_name', type: 'text' },
                  { label: 'Last Name*',  name: 'last_name',  type: 'text' },
                  { label: 'Email*',      name: 'email',      type: 'text' },
                ].map(({ label, name, type }) => (
                  <div key={name} className="col-md-3 mb-3" style={{ marginLeft: '8px' }}>
                    <label className="input-label" style={{ color: theme.colors.textPrimary }}>{label}</label>
                    <input type={type} name={name} value={formData[name]}
                      onChange={handleInputChange} onBlur={handleBlur}
                      className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
                      style={inputStyle(name)} required />
                    {errors[name] && <span className="invalid-feedback" style={{ color: theme.colors.danger }}>{errors[name]}</span>}
                    {name === 'email' && checking.email && (
                      <span style={{ color: theme.colors.primary, display: 'block', marginTop: '0.3rem' }}>Checking availability...</span>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button style={{ background: theme.gradients.secondary, color: theme.colors.textWhite, padding: '0.5rem 1.5rem', border: 'none', borderRadius: '5px', cursor: 'pointer' }} onClick={handlePrevious}>Previous</button>
                <button
                  style={{ background: theme.gradients.primary, color: theme.colors.textWhite, padding: '0.5rem 1.5rem', border: 'none', borderRadius: '5px', cursor: 'pointer', opacity: isLoading ? 0.6 : 1 }}
                  onClick={handleSubmit} disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddClient;